import React, { useState, useEffect } from 'react';

export default function TeacherDashboard({ user, onLogout }) {
  const [books, setBooks] = useState([]);
  const [students, setStudents] = useState([]);
  const [loans, setLoans] = useState([]);
  const [activeTab, setActiveTab] = useState('books');
  const [showModal, setShowModal] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [notification, setNotification] = useState(null);
  const [viewReviews, setViewReviews] = useState(null);
  const [bookReviews, setBookReviews] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    image_url: '',
    genres: '',
  });

  useEffect(() => {
    fetchBooks();
    fetchStudents();
    fetchLoans();
  }, []);

  const fetchBooks = async () => {
    setLoadingBooks(true);
    try {
      const response = await fetch('https://lupa.nekk.it/api/books');
      const data = await response.json();
      setBooks(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingBooks(false);
    }
  };

  const fetchStudents = async () => {
    const response = await fetch('https://lupa.nekk.it/api/users');
    const data = await response.json();
    setStudents(data.filter((u) => u.role === 'child'));
  };

  const fetchLoans = async () => {
    const response = await fetch('https://lupa.nekk.it/api/loans');
    const data = await response.json();
    setLoans(data);
  };

  const openCreateModal = () => {
    setEditingBook(null);
    setFormData({ title: '', author: '', image_url: '', genres: '' });
    setShowModal(true);
  };

  const openEditModal = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      author: book.author,
      image_url: book.image_url || '',
      genres: book.genres?.join(', ') || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const bookData = {
      ...formData,
      genres: formData.genres.split(',').map((g) => g.trim()).filter((g) => g),
    };

    try {
      if (editingBook) {
        await fetch(`https://lupa.nekk.it/api/books/${editingBook.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookData),
        });
      } else {
        await fetch('https://lupa.nekk.it/api/books', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookData),
        });
      }
      setShowModal(false);
      setNotification({ type: 'success', message: editingBook ? 'Libro actualizado' : 'Libro creado' });
      setTimeout(() => setNotification(null), 3000);
      fetchBooks();
    } catch (error) {
      setNotification({ type: 'error', message: 'Error al guardar el libro' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const fetchBookReviews = async (bookId) => {
    setLoadingReviews(true);
    try {
      const response = await fetch(`https://lupa.nekk.it/api/books/${bookId}/reviews`);
      const data = await response.json();
      setBookReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error:', error);
      setBookReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const openReviewsModal = async (book) => {
    setViewReviews(book);
    await fetchBookReviews(book.id);
  };

  const deleteReview = async (reviewId) => {
    setConfirmDialog({
      message: '¿Estás seguro de eliminar esta reseña?',
      onConfirm: async () => {
        try {
          await fetch(`https://lupa.nekk.it/api/reviews/${reviewId}`, { method: 'DELETE' });
          setNotification({ type: 'success', message: 'Reseña eliminada correctamente' });
          setTimeout(() => setNotification(null), 3000);
          // Refrescar las reseñas del libro actual
          if (viewReviews) {
            await fetchBookReviews(viewReviews.id);
          }
          fetchBooks(); // Actualizar avg_rating
        } catch (error) {
          setNotification({ type: 'error', message: 'Error al eliminar la reseña' });
          setTimeout(() => setNotification(null), 3000);
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const deleteBook = async (id) => {
    setConfirmDialog({
      message: '¿Estás seguro de eliminar este libro?',
      onConfirm: async () => {
        try {
          await fetch(`https://lupa.nekk.it/api/books/${id}`, { method: 'DELETE' });
          setNotification({ type: 'success', message: 'Libro eliminado correctamente' });
          setTimeout(() => setNotification(null), 3000);
          fetchBooks();
        } catch (error) {
          setNotification({ type: 'error', message: 'Error al eliminar el libro' });
          setTimeout(() => setNotification(null), 3000);
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <nav className="bg-purple-600 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold">🔍 Lupa Digital - Profesor {user.name}</h1>
          <button
            onClick={onLogout}
            className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded"
          >
            Cerrar Sesión
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6 w-full flex-grow">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('books')}
            className={`px-6 py-2 rounded-lg font-semibold ${
              activeTab === 'books'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700'
            }`}
          >
            Libros
          </button>
          <button
            onClick={() => setActiveTab('loans')}
            className={`px-6 py-2 rounded-lg font-semibold ${
              activeTab === 'loans'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700'
            }`}
          >
            Préstamos
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`px-6 py-2 rounded-lg font-semibold ${
              activeTab === 'students'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700'
            }`}
          >
            Estudiantes
          </button>
        </div>

        {activeTab === 'books' && (
          <div>
            <button
              onClick={openCreateModal}
              className="mb-6 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
            >
              + Agregar Libro
            </button>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {loadingBooks ? (
                // Skeleton Loading
                [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                    <div className="w-full h-40 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-3"></div>
                    <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2 w-3/4"></div>
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-3 w-2/3"></div>
                    <div className="space-y-2">
                      <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                      <div className="flex gap-2">
                        <div className="flex-1 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                        <div className="flex-1 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                books.map((book) => (
                <div key={book.id} className="bg-white rounded-lg shadow p-4">
                  {book.image_url && (
                    <img
                      src={book.image_url}
                      alt={book.title}
                      className="w-full h-40 object-contain rounded mb-3 bg-gray-50"
                    />
                  )}
                  <h3 className="text-lg font-bold mb-1 line-clamp-2">{book.title}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-1">Autor: {book.author}</p>
                  {book.genres?.length > 0 && (
                    <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                      {book.genres.join(', ')}
                    </p>
                  )}
                  {book.avg_rating > 0 && (
                    <p className="text-orange-600 text-sm font-semibold mb-2">
                      {'🔍'.repeat(Math.round(book.avg_rating))} ({book.avg_rating.toFixed(1)})
                    </p>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{book.in_stock ? '🟢' : '🔴'}</span>
                    <span className="text-xs font-semibold">
                      {book.in_stock ? 'Disponible' : 'Prestado'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={() => openReviewsModal(book)}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold"
                    >
                      🔍 Ver reseñas
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(book)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => deleteBook(book.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
                )))}
            </div>
          </div>
        )}

        {activeTab === 'loans' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Estudiante
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Libro
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Fecha Préstamo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loans.map((loan) => (
                  <tr key={loan.id}>
                    <td className="px-6 py-4">{loan.user?.name}</td>
                    <td className="px-6 py-4">{loan.book?.title}</td>
                    <td className="px-6 py-4">
                      {new Date(loan.borrowed_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded ${
                          loan.status === 'active'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {loan.status === 'active' ? 'Activo' : 'Devuelto'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {students.map((student) => {
              const studentLoans = loans.filter(
                (l) => l.user_id === student.id && l.status === 'active'
              );
              return (
                <div key={student.id} className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-xl font-bold mb-2">{student.name}</h3>
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">
                      Libros actuales ({studentLoans.length})
                    </h4>
                    {studentLoans.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        No tiene libros prestados
                      </p>
                    ) : (
                      <ul className="text-sm space-y-1">
                        {studentLoans.map((loan) => (
                          <li key={loan.id} className="text-gray-700">
                            • {loan.book?.title}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {notification && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-6 py-4 rounded-lg shadow-xl font-semibold text-white ${
            notification.type === 'success' 
              ? 'bg-green-600' 
              : 'bg-red-600'
          }`}>
            {notification.message}
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {viewReviews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                🔍 Reseñas de "{viewReviews.title}"
              </h2>
              <button
                onClick={() => setViewReviews(null)}
                className="text-gray-600 hover:text-gray-800 text-2xl font-bold"
              >
                ✕
              </button>
            </div>
            {bookReviews.length === 0 && !loadingReviews ? (
              <p className="text-center text-gray-600 text-lg py-8">
                Todavía no hay reseñas para este libro
              </p>
            ) : loadingReviews ? (
              // Skeleton Loading para reseñas
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 shadow border border-gray-200 animate-pulse">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 mb-2"></div>
                      <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
                    </div>
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-5/6"></div>
                  </div>
                </div>
              ))
            ) : (
              <div className="space-y-4">
                {Array.isArray(bookReviews) && bookReviews.map((review) => (
                  <div key={review.id} className="bg-gray-50 rounded-lg p-4 shadow border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold text-lg text-purple-700">👤 {review.user?.name}</p>
                        <p className="text-orange-600 text-lg">
                          {'🔍'.repeat(review.rating)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <p className="text-xs text-gray-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                        <button
                          onClick={() => deleteReview(review.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-semibold"
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-700">{review.review}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {confirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-xl font-bold mb-4">Confirmar acción</h3>
            <p className="text-gray-700 mb-6">{confirmDialog.message}</p>
            <div className="flex gap-2">
              <button
                onClick={confirmDialog.onConfirm}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold"
              >
                Sí, eliminar
              </button>
              <button
                onClick={confirmDialog.onCancel}
                className="flex-1 bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded font-semibold"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">
              {editingBook ? 'Editar Libro' : 'Agregar Libro'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Título</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Autor</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) =>
                    setFormData({ ...formData, author: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Géneros (separados por coma)
                </label>
                <input
                  type="text"
                  value={formData.genres}
                  onChange={(e) =>
                    setFormData({ ...formData, genres: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded"
                  placeholder="Ficción, Aventura, Fantasía"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                >
                  {editingBook ? 'Guardar' : 'Crear'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 mt-auto w-full">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-lg font-semibold">✨ Creado por Ariadna Nadal ✨</p>
        </div>
      </footer>
    </div>
  );
}
